import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { wantToTryAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';

/**
 * WichListContext
 *
 * Single source of truth for:
 * - membershipMap: sandwich_id -> boolean
 * - count: total items in the current user's WichList (from /meta)
 *
 * Public hook API (via useWichList):
 * - isInWichList(id: string | number): boolean
 * - ensureMembership(ids: (string | number)[]): Promise<void>
 * - toggleWichList(id: string | number, options?: { sourceReviewId?: string; source?: string }): Promise<boolean>
 * - count: number | null
 * - refreshMeta(): Promise<void>
 * - membershipLoading: boolean
 * - countLoading: boolean
 * - primeMembership(id: string | number, isMember: boolean): void  // helper to seed state from existing props
 */

const WichListContext = createContext(null);

export const WichListProvider = ({ children }) => {
  const { user } = useAuth();
  const [membershipMap, setMembershipMap] = useState({});
  const [count, setCount] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [countLoading, setCountLoading] = useState(false);
  const lastUserIdRef = useRef(null);

  // Reset state when user logs out or switches accounts
  useEffect(() => {
    const currentUserId = user?.id || null;
    if (!currentUserId) {
      setMembershipMap({});
      setCount(null);
    } else if (lastUserIdRef.current && lastUserIdRef.current !== currentUserId) {
      setMembershipMap({});
      setCount(null);
    }
    lastUserIdRef.current = currentUserId;
  }, [user]);

  const isInWichList = useCallback(
    (sandwichId) => {
      if (!user) return false;
      if (sandwichId == null) return false;
      const key = String(sandwichId);
      return !!membershipMap[key];
    },
    [membershipMap, user]
  );

  const ensureMembership = useCallback(
    async (ids) => {
      if (!user) return;
      if (!Array.isArray(ids) || ids.length === 0) return;

      const idsToCheckSet = new Set();
      ids.forEach((id) => {
        if (id == null) return;
        const key = String(id);
        if (!(key in membershipMap)) {
          idsToCheckSet.add(key);
        }
      });

      if (idsToCheckSet.size === 0) {
        return;
      }

      setMembershipLoading(true);
      try {
        const uniqueIds = Array.from(idsToCheckSet);
        const containsMap = await wantToTryAPI.checkContains(uniqueIds);

        setMembershipMap((prev) => {
          const next = { ...prev };
          // Merge explicit results
          if (containsMap && typeof containsMap === 'object') {
            Object.entries(containsMap).forEach(([key, value]) => {
              next[String(key)] = !!value;
            });
          }
          // Any ID we asked about that wasn't returned should be treated as false
          uniqueIds.forEach((key) => {
            if (!(key in next)) {
              next[key] = false;
            }
          });
          return next;
        });
      } catch (error) {
        // Log and allow callers to decide how to surface errors
        // Callers should still render a safe default (inactive buttons).
        console.error('Failed to ensure WichList membership', error);
      } finally {
        setMembershipLoading(false);
      }
    },
    [membershipMap, user]
  );

  const toggleWichList = useCallback(
    async (sandwichId, options = {}) => {
      if (!user) {
        throw new Error('Must be logged in to modify WichList');
      }
      if (sandwichId == null) {
        throw new Error('sandwichId is required for toggleWichList');
      }

      const key = String(sandwichId);
      const previousActive = !!membershipMap[key];
      const nextActive = !previousActive;

      // Optimistic update: flip membership and adjust count
      setMembershipMap((prev) => ({
        ...prev,
        [key]: nextActive
      }));
      setCount((prevCount) => {
        if (prevCount == null) return prevCount;
        const delta = nextActive ? 1 : -1;
        return Math.max(0, prevCount + delta);
      });

      try {
        await wantToTryAPI.toggle(sandwichId, nextActive, {
          sourceReviewId: options.sourceReviewId
        });
        return nextActive;
      } catch (error) {
        console.error('Failed to toggle WichList membership', error);
        // Revert optimistic update
        setMembershipMap((prev) => ({
          ...prev,
          [key]: previousActive
        }));
        setCount((prevCount) => {
          if (prevCount == null) return prevCount;
          const delta = nextActive ? -1 : 1;
          return Math.max(0, prevCount + delta);
        });
        throw error;
      }
    },
    [membershipMap, user]
  );

  const refreshMeta = useCallback(async () => {
    if (!user) {
      setCount(null);
      return;
    }

    setCountLoading(true);
    try {
      const response = await wantToTryAPI.getMeta();
      const itemsCount = response?.items_count;
      setCount(typeof itemsCount === 'number' ? itemsCount : 0);
    } catch (error) {
      console.error('Failed to fetch WichList meta', error);
    } finally {
      setCountLoading(false);
    }
  }, [user]);

  /**
   * Prime membership state from an external initial value (e.g. existing props).
   * This is useful while migrating existing surfaces that already know membership
   * via their own batch calls.
   */
  const primeMembership = useCallback(
    (sandwichId, isMember) => {
      if (!user) return;
      if (sandwichId == null) return;
      const key = String(sandwichId);
      setMembershipMap((prev) => {
        if (key in prev) return prev;
        return {
          ...prev,
          [key]: !!isMember
        };
      });
    },
    [user]
  );

  const value = useMemo(
    () => ({
      membershipMap,
      isInWichList,
      ensureMembership,
      toggleWichList,
      count,
      refreshMeta,
      membershipLoading,
      countLoading,
      primeMembership
    }),
    [
      membershipMap,
      isInWichList,
      ensureMembership,
      toggleWichList,
      count,
      refreshMeta,
      membershipLoading,
      countLoading,
      primeMembership
    ]
  );

  return <WichListContext.Provider value={value}>{children}</WichListContext.Provider>;
};

export const useWichList = () => {
  const context = useContext(WichListContext);
  if (!context) {
    throw new Error('useWichList must be used within a WichListProvider');
  }
  return context;
};


